import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  type DataTableLoadingState,
} from "../useDataTableState";

import styles from "./DataTablePagination.module.css";

interface PaginationFooterProps {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  currentPage: number;
  nextPage: () => void;
  prevPage: () => void;
  hasNextPage: boolean;
  canGetPrevPage: boolean;

  /**
   * Total rows across all pages.
   * When `undefined` the row-range label is hidden.
   */
  totalRowCount: number | undefined;

  /**
   * Reflects the current data-fetch state. Any non-`"idle"` value disables
   * the next-page button so the user can't queue another advance while one
   * is in flight. Defaults to `"idle"`.
   */
  loading?: DataTableLoadingState;
}

/**
 * Footer rendered beneath the AG Grid surface inside `DataTable.Table`.
 * Hosts the page-size selector, the current row range (e.g. `1-25 of 312`),
 * and prev/next page buttons.
 *
 * Internal to the DataTable primitive — consumers don't import this directly;
 * `DataTable.Table` mounts it.
 */
export function PaginationFooter(props: PaginationFooterProps) {
  const {
    hasNextPage,
    canGetPrevPage,
    loading = "idle",
    nextPage,
    onPageSizeChange,
    currentPage,
    pageSize,
    prevPage,
    totalRowCount,
  } = props;

  const isFetching = loading !== "idle";
  const hasTotal = totalRowCount !== undefined;

  const rangeStart = totalRowCount === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = hasTotal
    ? Math.min((currentPage + 1) * pageSize, totalRowCount)
    : 0;

  // Defensive fallback if a saved pagesize is outside the options.
  // Better this than a blank value. Sorted to keep ascending order.
  const pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS.includes(
    pageSize as (typeof DEFAULT_PAGE_SIZE_OPTIONS)[number],
  )
    ? DEFAULT_PAGE_SIZE_OPTIONS
    : [...DEFAULT_PAGE_SIZE_OPTIONS, pageSize].sort((a, b) => a - b);

  return (
    <div className={styles.footer}>
      <div className={styles.left}>
        <span className={styles.label}>Rows per page</span>
        <select
          className={styles.pageSize}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        {hasTotal && (
          <span className={styles.range}>
            {`${rangeStart}-${rangeEnd} of ${totalRowCount}`}
          </span>
        )}
      </div>

      <div className={styles.right}>
        <button
          aria-label="Previous page"
          className={styles.iconButton}
          // Backward navigation always uses an already resolved cursor
          // so no need to gate on isFetching.
          disabled={!canGetPrevPage}
          onClick={prevPage}
        >
          <ChevronLeft />
        </button>
        <button
          aria-label="Next page"
          className={styles.iconButton}
          disabled={!hasNextPage || isFetching}
          onClick={nextPage}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
}
